import React from 'react';
import { useAuthStore } from '../../stores/authStore';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../ui/Button/Button';
import { ChevronDown, AlertTriangle, List, CreditCard, User, Home, Building2, LogOut } from 'lucide-react';
import { 
  DropdownMenu, 
  DropdownMenuTrigger, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator 
} from '../ui/Dropdown/Dropdown';
import { Avatar } from '../ui/Avatar/Avatar';
import { Badge } from '../ui/Badge/Badge';
import { useOrgStore } from '../../stores/orgStore';
import { BillingService, type SubscriptionSnapshotDto } from '@/services/BillingService';

export function UserMenu() {
  const user = useAuthStore(s => s.user);
  const logout = useAuthStore(s => s.logout);
  const isLoading = useAuthStore(s => s.isLoading);
  const orgs = useOrgStore(s => s.orgs);
  const activeOrgId = useOrgStore(s => s.activeOrgId);
  const setActiveOrgId = useOrgStore(s => s.setActiveOrgId);
  const fetchOrgs = useOrgStore(s => s.fetchOrgs);
  const orgsInitialized = useOrgStore(s => s.initialized);
  const isSuperAdmin = !!(user as any)?.isSuperAdmin;
  const hasAdminAccess = isSuperAdmin || !!(user as any)?.adminRole;
  const navigate = useNavigate();
  const [subscription, setSubscription] = React.useState<SubscriptionSnapshotDto | null>(null);
  const [portalLoading, setPortalLoading] = React.useState(false);
  const billingService = React.useMemo(() => new BillingService(), []);

  React.useEffect(() => {
    if (user && !orgsInitialized) {
      try { fetchOrgs(); } catch {}
    }
  }, [user, orgsInitialized]);

  React.useEffect(() => {
    if (activeOrgId) {
      billingService.getSubscription()
        .then(sub => setSubscription(sub))
        .catch(() => setSubscription(null));
    }
  }, [activeOrgId, billingService]);

  if (!user) return null;

  const userInitial = user.fullName?.charAt(0).toUpperCase() || user.email.charAt(0).toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="flex items-center gap-2">
          <Avatar 
            size="sm" 
            initials={userInitial}
            name={user.fullName || user.email}
          />
          <span className="font-medium hidden sm:inline">{user.fullName || user.email}</span>
          <ChevronDown className="w-4 h-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <div className="px-3 py-2">
          <div className="text-sm font-medium">{user.fullName}</div>
          <div className="text-xs text-muted-foreground">{user.email}</div>
          {!user.isEmailVerified && (
            <Badge variant="warning" className="mt-2 text-xs">
              <AlertTriangle className="w-3 h-3 mr-1" />
              Email not verified
            </Badge>
          )}
        </div>
        {hasAdminAccess && (
          <DropdownMenuItem asChild>
            <Link to="/admin" className="flex items-center cursor-pointer">
              <List className="w-4 h-4 mr-2" />
              Admin Panel
            </Link>
          </DropdownMenuItem>
        )}
        {orgs.length > 0 && (
          <>
            <div className="px-3 py-2 border-t">
              <div className="text-xs mb-1 text-muted-foreground">Organization</div>
              <select
                className="w-full text-sm bg-background border rounded px-2 py-1"
                value={activeOrgId || ''}
                onChange={(e) => setActiveOrgId(e.target.value)}
              >
                {orgs.map(o => (
                  <option key={o.id} value={o.id}>{o.name}</option>
                ))}
              </select>
            </div>
            {subscription && (
              <div className="px-3 py-2 border-t">
                <div className="text-xs mb-1 text-muted-foreground">Subscription</div>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">
                    {subscription.plan?.name || 'Free'}
                  </span>
                  {subscription.status === 'trialing' && (
                    <Badge variant="default" className="text-xs">Trial</Badge>
                  )}
                  {subscription.status === 'active' && (
                    <Badge variant="success" className="text-xs">Active</Badge>
                  )}
                  {subscription.status === 'past_due' && (
                    <Badge variant="warning" className="text-xs">Past Due</Badge>
                  )}
                </div>
                {subscription.currentPeriodEnd && (
                  <div className="text-xs text-muted-foreground mt-1">
                    {subscription.status === 'trialing' ? 'Trial ends' : 'Renews'} {new Date(subscription.currentPeriodEnd).toLocaleDateString()}
                  </div>
                )}
                {subscription.stripeCustomerId && (
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      setPortalLoading(true);
                      try {
                        const { url } = await billingService.createPortalSession();
                        window.open(url, '_blank');
                      } catch (err) {
                        console.error('Failed to open customer portal:', err);
                      } finally {
                        setPortalLoading(false);
                      }
                    }}
                    disabled={portalLoading}
                    className="mt-2 w-full flex items-center justify-center gap-1.5 px-2 py-1 text-xs font-medium rounded border border-gray-200 bg-white hover:bg-gray-50 transition-colors text-foreground disabled:opacity-50"
                  >
                    <CreditCard className="w-3.5 h-3.5" />
                    {portalLoading ? 'Opening…' : 'Manage Subscription'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
        <DropdownMenuItem asChild>
          <Link to="/settings/account/profile" className="flex items-center cursor-pointer">
            <User className="w-4 h-4 mr-2" />
            Account Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/dashboard" className="flex items-center cursor-pointer">
            <Home className="w-4 h-4 mr-2" />
            Go to Workspace
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to="/organization" className="flex items-center cursor-pointer">
            <Building2 className="w-4 h-4 mr-2" />
            Organization Settings
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={async () => {
            try {
              await logout();
            } finally {
              navigate('/auth', { replace: true });
            }
          }}
          disabled={isLoading}
          className="cursor-pointer"
        >
          <LogOut className="w-4 h-4 mr-2" />
          {isLoading ? 'Signing out...' : 'Sign out'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
